/**
 * Status effect system — burn, slow, stun.
 * Also defines DamageType (alias of WeaponCategory) and per-type colors.
 */
import type { WeaponCategory } from '../upgrades/WeaponData';
import type { Enemy } from './Entity';

// ─── Damage types ──────────────────────────────────────────

export type DamageType = WeaponCategory;

/** Canonical color per damage type — used for floating text, VFX tinting. */
export const DAMAGE_TYPE_COLORS: Record<DamageType, number> = {
  ballistic: 0xddaa44,
  energy:    0x44ccff,
  rocket:    0xff8833,
  bomb:      0xff4400,
  melee:     0xffaa00,
};

// ─── Status effects ────────────────────────────────────────

export type StatusEffectType = 'burn' | 'slow' | 'stun';

export interface StatusEffect {
  type: StatusEffectType;
  /** Seconds remaining */
  remaining: number;
  /** Original duration (for progress calc) */
  duration: number;
  /** Effect strength — burn: damage/sec, slow: fraction (0.3 = -30%), stun: unused */
  magnitude: number;
  /** Which damage type applied this effect */
  source: DamageType;
}

/** Max simultaneous effects per enemy */
const MAX_EFFECTS = 5;
/** Max burn stacks */
const MAX_BURN_STACKS = 3;

// ─── Apply / query helpers ─────────────────────────────────

/**
 * Apply a status effect to an enemy with stacking rules:
 * - burn: stacks up to MAX_BURN_STACKS (refreshes duration, increases magnitude)
 * - slow: takes strongest (refreshes duration if stronger or equal)
 * - stun: refreshes duration if longer
 */
export function applyStatusEffect(enemy: Enemy, effect: StatusEffect) {
  const existing = enemy.statusEffects;

  if (effect.type === 'burn') {
    // Count current burn stacks (avoid .filter() allocation)
    let burnCount = 0;
    let weakest: StatusEffect | null = null;
    for (let i = 0; i < existing.length; i++) {
      const cur = existing[i]!;
      if (cur.type === 'burn') {
        burnCount++;
        if (!weakest || cur.magnitude < weakest.magnitude) {
          weakest = cur;
        }
      }
    }
    if (burnCount < MAX_BURN_STACKS) {
      // Add new stack — evict shortest-remaining non-burn effect if at capacity
      if (existing.length >= MAX_EFFECTS) {
        let evictIdx = -1;
        let shortest = Infinity;
        for (let i = 0; i < existing.length; i++) {
          const cur = existing[i]!;
          if (cur.type !== 'burn' && cur.remaining < shortest) {
            shortest = cur.remaining;
            evictIdx = i;
          }
        }
        if (evictIdx >= 0) existing.splice(evictIdx, 1);
      }
      if (existing.length < MAX_EFFECTS) {
        existing.push({ ...effect });
      }
    } else if (weakest) {
      // Refresh weakest burn stack
      weakest.remaining = effect.duration;
      weakest.duration = effect.duration;
      weakest.magnitude = Math.max(weakest.magnitude, effect.magnitude);
    }
    return;
  }

  // slow / stun — find existing of same type.
  // Hand-rolled loop avoids the arrow-fn allocation that `findIndex` forces
  // on a hot path (every projectile hit calls this).
  let idx = -1;
  for (let i = 0; i < existing.length; i++) {
    if (existing[i]!.type === effect.type) { idx = i; break; }
  }
  if (idx >= 0) {
    const cur = existing[idx]!;
    if (effect.type === 'slow') {
      // Take strongest magnitude, refresh duration
      if (effect.magnitude >= cur.magnitude) {
        cur.magnitude = effect.magnitude;
        cur.remaining = effect.duration;
        cur.duration = effect.duration;
      }
    } else {
      // stun — refresh if longer
      if (effect.remaining >= cur.remaining) {
        cur.remaining = effect.duration;
        cur.duration = effect.duration;
      }
    }
  } else if (existing.length < MAX_EFFECTS) {
    existing.push({ ...effect });
  }
}

/** Check if an enemy currently has a specific effect type. */
export function hasStatusEffect(enemy: Enemy, type: StatusEffectType): boolean {
  const effects = enemy.statusEffects;
  for (let i = 0; i < effects.length; i++) {
    if (effects[i]!.type === type) return true;
  }
  return false;
}

/** Get the strongest effect of a given type (or null). */
export function getStatusEffect(enemy: Enemy, type: StatusEffectType): StatusEffect | null {
  let best: StatusEffect | null = null;
  for (const e of enemy.statusEffects) {
    if (e.type === type && (!best || e.magnitude > best.magnitude)) best = e;
  }
  return best;
}

// ─── Per-frame update ──────────────────────────────────────

export interface StatusTickResult {
  burnDamage: number;
}

/**
 * Tick all status effects on an enemy. Call once per frame.
 * - Burns deal damage (returned so caller can apply it + floating text).
 * - Expired effects are removed.
 * - Slow/stun are read by movement/combat code directly.
 */
export function updateStatusEffects(enemy: Enemy, dt: number): StatusTickResult {
  let burnDamage = 0;
  const effects = enemy.statusEffects;

  for (let i = effects.length - 1; i >= 0; i--) {
    const e = effects[i]!;
    e.remaining -= dt;

    if (e.type === 'burn') {
      burnDamage += e.magnitude * dt;
    }

    if (e.remaining <= 0) {
      effects.splice(i, 1);
    }
  }

  return { burnDamage };
}

// ─── Tier effect table ─────────────────────────────────────

export interface TierEffectParams {
  type: StatusEffectType;
  duration: number;
  magnitude: number;
}

/**
 * Get the secondary effect for a weapon category at a given tier.
 * Returns null for T1-2 (no secondary effect).
 * Ballistic uses armor pierce which is handled separately (not a status effect).
 */
export function getSecondaryEffect(category: DamageType, tier: number): TierEffectParams | null {
  if (tier < 3) return null;

  switch (category) {
    case 'energy':
      // Burn DOT — scales with tier
      return {
        type: 'burn',
        duration: tier === 3 ? 3 : tier === 4 ? 4 : 5,
        magnitude: tier === 3 ? 2 : tier === 4 ? 3 : 4,
      };
    case 'bomb':
      // Slow — scales with tier
      return {
        type: 'slow',
        duration: tier === 3 ? 3 : tier === 4 ? 4 : 5,
        magnitude: tier === 3 ? 0.3 : tier === 4 ? 0.5 : 0.7,
      };
    case 'melee':
      // Stun — scales with tier
      return {
        type: 'stun',
        duration: tier === 3 ? 0.5 : tier === 4 ? 0.8 : 1.0,
        magnitude: 1,
      };
    default:
      // ballistic / rocket — handled elsewhere (armor pierce / knockback boost)
      return null;
  }
}

/** Armor pierce fraction for ballistic weapons by tier. 0 for T1-2. */
export function getArmorPierce(tier: number): number {
  if (tier < 3) return 0;
  return tier === 3 ? 0.15 : tier === 4 ? 0.25 : 0.35;
}
